import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Copy, Pencil, Check, PackagePlus, Eye } from "lucide-react";
import ProductPicker from "@/components/ProductPicker";
import InlineProductCreateModal from "@/components/InlineProductCreateModal";
import { NumberField } from "@/components/document-form/NumberField";

const formatEur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(v) || 0);


/**
 * Auto-resizing textarea. Grandit de 2 a 6 lignes selon le contenu.
 */
function AutoTextarea({ value, onChange, placeholder, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "0px";
      const scrollH = ref.current.scrollHeight;
      const min = 2 * 20; // ~2 lignes
      const max = 6 * 20; // ~6 lignes
      ref.current.style.height = `${Math.min(Math.max(scrollH, min), max)}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 leading-5 resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      style={{ minHeight: "40px" }}
    />
  );
}

/**
 * Editeur des lignes de document. Chaque ligne :
 *  - Champ produit sur toute la largeur (ProductPicker)
 *  - Reference imprimee (sous le produit)
 *  - Description multi-lignes (textarea auto-extensible, c'est ce qui s'imprime)
 *  - Rangee alignee a droite : qty x unit_price - TVA - Total HT - actions
 * Une ligne validee (produit choisi ET quantite/prix > 0) s'affiche compacte,
 * seule la ligne en cours d'edition est mise en evidence.
 */
export function DocumentItemsEditor({ items, products, onAdd, onUpdate, onRemove, onDuplicate, onProductCreated, showPreview, onTogglePreview }) {
  const [editingIdx, setEditingIdx] = useState(items.length === 0 ? null : items.length - 1);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [pendingLineIdx, setPendingLineIdx] = useState(null);
  const [pendingQuery, setPendingQuery] = useState("");

  // A l'ajout d'une ligne : passer en edition sur la derniere
  useEffect(() => {
    if (items.length > 0) {
      setEditingIdx((prev) => (prev == null || prev >= items.length ? items.length - 1 : prev));
    } else {
      setEditingIdx(null);
    }
  }, [items.length]);

  const handleProductSelect = useCallback((idx, product) => {
    // Sync inconditionnelle : quand on lie un produit a une ligne, on ecrase
    // les valeurs par defaut de la ligne (TVA 20, prix 0) par celles du produit.
    onUpdate(idx, "product_id", product.id);
    if (product.name) onUpdate(idx, "product_name", product.name);
    if (product.description != null) onUpdate(idx, "description", product.description);
    if (product.reference) onUpdate(idx, "reference", product.reference);
    if (product.unit_price != null) onUpdate(idx, "unit_price", product.unit_price);
    if (product.tva_rate != null) onUpdate(idx, "tva_rate", product.tva_rate);
  }, [onUpdate]);

  const handleCreateFromPicker = (idx, query) => {
    setPendingLineIdx(idx);
    setPendingQuery(query);
    setProductModalOpen(true);
  };

  const handleProductCreated = (createdProduct) => {
    setProductModalOpen(false);
    if (pendingLineIdx != null) {
      handleProductSelect(pendingLineIdx, createdProduct);
    }
    if (onProductCreated) onProductCreated(createdProduct);
    setPendingLineIdx(null);
    setPendingQuery("");
  };

  const handleAddLine = () => {
    onAdd();
    // Basculer editingIdx sur la nouvelle ligne (ajoutee en fin)
    // NB: items.length ici correspond a l'ancien length, donc = index de la nouvelle
    setEditingIdx(items.length);
  };

  const handleKeyDownLast = (e, idx) => {
    if (e.key === "Enter" && !e.shiftKey && idx === items.length - 1) {
      e.preventDefault();
      handleAddLine();
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <Label>Lignes</Label>
        <div className="flex items-center gap-2 flex-wrap">
          {onTogglePreview && (
            <Button variant="outline" size="sm" type="button" onClick={onTogglePreview} data-testid="toggle-preview-btn" title="Afficher/masquer l'aperçu">
              <Eye size={14} className="mr-1" /> Aperçu{showPreview ? " (masquer)" : ""}
            </Button>
          )}
          <Button variant="outline" size="sm" type="button" onClick={() => setProductModalOpen(true)} data-testid="new-product-catalog-btn">
            <PackagePlus size={14} className="mr-1" /> Créer un produit au catalogue
          </Button>
          <Button variant="outline" size="sm" type="button" onClick={handleAddLine} data-testid="add-item-btn">
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((item, idx) => {
          const linkedProduct = products.find(p => p.id === item.product_id);
          const ref = linkedProduct?.reference || item.reference || "";
          const isEditing = editingIdx === idx;
          const qty = Number(item.quantity) || 0;
          const price = Number(item.unit_price) || 0;
          const totalHt = qty * price;
          const isValid = (item.product_name || item.product_id) && qty > 0 && price >= 0;

          if (!isEditing && isValid) {
            // ==== Ligne validée (compact) ====
            // Mobile : carte empilée (produit dessus, chiffres+actions dessous).
            // Desktop (sm+) : layout original en flex-row.
            return (
              <div
                key={item._key || item.product_id || `item-${idx}`}
                className="border rounded-md px-3 py-2 bg-white hover:bg-slate-50 transition-colors"
                data-testid={`line-collapsed-${idx}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 break-words">{item.product_name || <span className="text-slate-400">(sans nom)</span>}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {ref && <span className="font-mono">Réf. {ref}</span>}
                      {ref && item.description && <span className="mx-1.5">·</span>}
                      {item.description && <span className="whitespace-pre-line">{item.description}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-4 text-sm border-t sm:border-t-0 pt-2 sm:pt-0 sm:shrink-0">
                    {/* Chiffres + total : contigus sur mobile, éclatés dans le flex desktop via sm:contents */}
                    <div className="flex sm:contents items-center justify-between gap-3">
                      <div className="text-slate-600 font-mono text-xs sm:text-sm sm:whitespace-nowrap">
                        {qty} × {formatEur(price)} · TVA {item.tva_rate || 20}%
                      </div>
                      <div className="font-semibold text-slate-900 sm:min-w-[92px] text-right font-mono">
                        {formatEur(totalHt)}
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" type="button" className="h-8 w-8" onClick={() => setEditingIdx(idx)} title="Modifier" data-testid={`line-edit-${idx}`}>
                        <Pencil size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" type="button" className="h-8 w-8" onClick={() => onDuplicate?.(idx)} title="Dupliquer" data-testid={`line-duplicate-${idx}`}>
                        <Copy size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" type="button" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => onRemove(idx)} title="Supprimer" data-testid={`line-remove-${idx}`}>
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          // ==== Ligne en cours d'edition ====
          return (
            <div
              key={item._key || item.product_id || `item-${idx}`}
              className="border-2 border-indigo-300 bg-indigo-50/40 rounded-md p-3"
              data-testid={`line-editing-${idx}`}
            >
              {/* Produit sur toute la largeur */}
              <div>
                <ProductPicker
                  value={linkedProduct?.name || item.product_name || ""}
                  onSelect={(p) => handleProductSelect(idx, p)}
                  onCreateNew={(q) => handleCreateFromPicker(idx, q)}
                />
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                  {ref ? (
                    <span className="font-mono">Réf. {ref}</span>
                  ) : (
                    <span className="text-slate-300">(sans référence)</span>
                  )}
                  <span className="text-slate-300">·</span>
                  <span>désignation imprimée sur le document</span>
                </div>
              </div>

              {/* Description multi-lignes */}
              <div className="mt-2">
                <AutoTextarea
                  value={item.description || ""}
                  onChange={(e) => onUpdate(idx, "description", e.target.value)}
                  placeholder="Détail imprimé sur le document (sauts de ligne et puces conservés)&#10;• marquage&#10;• visuel"
                  testid={`item-description-${idx}`}
                />
              </div>

              {/* Rangée : qty, prix, tva, total, actions
                  Mobile 375 px : grille 2 colonnes pour lisibilité.
                  Desktop (sm+) : alignement à droite en flex. */}
              <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-end sm:justify-end sm:gap-3">
                <div className="sm:w-24">
                  <Label className="text-[10px] uppercase text-slate-500 mb-0.5 block">Qté</Label>
                  <NumberField
                    className="h-9 text-sm text-right font-mono"
                    value={item.quantity}
                    onValueChange={(v) => onUpdate(idx, "quantity", typeof v === "number" ? v : 0)}
                    testid={`item-quantity-${idx}`}
                  />
                </div>
                <div className="sm:w-32">
                  <Label className="text-[10px] uppercase text-slate-500 mb-0.5 block">Prix unit. HT</Label>
                  <NumberField
                    className="h-9 text-sm text-right font-mono"
                    value={item.unit_price}
                    onValueChange={(v) => onUpdate(idx, "unit_price", typeof v === "number" ? v : 0)}
                    testid={`item-unit-price-${idx}`}
                  />
                </div>
                <div className="sm:w-20">
                  <Label className="text-[10px] uppercase text-slate-500 mb-0.5 block">TVA&nbsp;%</Label>
                  <NumberField
                    className="h-9 text-sm text-right font-mono"
                    value={item.tva_rate}
                    onValueChange={(v) => onUpdate(idx, "tva_rate", typeof v === "number" ? v : 0)}
                    onKeyDown={(e) => handleKeyDownLast(e, idx)}
                    testid={`item-tva-rate-${idx}`}
                  />
                </div>
                <div className="sm:w-32">
                  <Label className="text-[10px] uppercase text-slate-500 mb-0.5 block">Total HT</Label>
                  <div className="h-9 flex items-center justify-end px-2 font-mono font-semibold text-slate-900 rounded bg-white border border-slate-200" data-testid={`item-total-${idx}`}>
                    {formatEur(totalHt)}
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1 pt-1 sm:pt-0 sm:pb-0.5 border-t sm:border-t-0">
                  <Button variant="ghost" size="icon" type="button" className="h-8 w-8" onClick={() => setEditingIdx(null)} title="Valider la ligne" data-testid={`line-validate-${idx}`}>
                    <Check size={16} className="text-indigo-600" />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" className="h-8 w-8" onClick={() => onDuplicate?.(idx)} title="Dupliquer" data-testid={`line-duplicate-${idx}`}>
                    <Copy size={15} />
                  </Button>
                  <Button variant="ghost" size="icon" type="button" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => onRemove(idx)} title="Supprimer" data-testid={`line-remove-${idx}`}>
                    <Trash2 size={15} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-slate-400 italic py-3 text-center">Aucune ligne. Cliquez sur « Ajouter ».</p>
        )}
      </div>

      <InlineProductCreateModal
        open={productModalOpen}
        initialName={pendingQuery}
        onClose={() => { setProductModalOpen(false); setPendingLineIdx(null); setPendingQuery(""); }}
        onCreated={handleProductCreated}
      />
    </div>
  );
}
